import { Component, OnInit, ChangeDetectorRef, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { EventDetailViewModel } from "../../shared/models/event.model";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { CoreLogService } from "../../services/log.service";
import { ActivatedRoute } from "@angular/router";
import { LikeService } from "../../services/like.service";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { RatingService } from "../../services/rating.service";
import { Subscription } from "rxjs";
import { ContentType, SnackBarMessageType } from "../../shared/contentTypes";
import { EventDetailService } from "./eventDetail.service";
import { Moment } from "moment";
import * as moment from "moment";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { floatingText } from "../../shared/animation/component.animation";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { RatingDialogComponent } from "../../modalDialogs/ratingdialog/ratingDialog.component";
import { RouterHelperService } from "../../services/routerHelper.service";

@Component({
  selector: "eventdetail",
  templateUrl: "./eventDetail.component.html",
  styleUrls: ["./eventDetail.component.scss"],
  providers: [EventDetailService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [floatingText]
})
export class EventDetailComponent implements OnInit, OnDestroy {

  // set animation movement
  public floatingTextActivated: boolean = false; // to activate animation if header in wider then the width of page.
  public animationObject = { value: "left", params: { shiftPixels: "0px", initialShift: "0px", initialtime: "0s", startPosition: "0px" } };
  private shiftPixels = 0; // used for shift pixel in floating animation

  public hasData: boolean = true; // check if there is data for this page;
  public panelOpenState: boolean = false;
  private eventId: number | string;
  public eventViewModel: EventDetailViewModel;
  public fullDaysPanelOpened: boolean;
  public contentType = ContentType;

  private _langSubscriber: Subscription;
  private _ratingChangesSubscriber: Subscription;
  private _eventChangesSubscriber: Subscription;

  constructor(private _eventService: EventDetailService
    , private _internationalisationService: InternationalisationService
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _activatedRoute: ActivatedRoute
    , private _ratingService: RatingService
    , private _snackBarService: SnackBarService
    , private _likeService: LikeService
    , private _routerHelperService: RouterHelperService
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _bottomSheetHelperService: BottomSheetHelperService) {
    _changeDetectorRef.detach();
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("EventDetailComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me._activatedRoute.paramMap.subscribe(params => {
      me.eventId = params.get("id");
    });

    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    // reload event data if data has been changed in storage.
    me._eventChangesSubscriber = me._eventService.ObserveChanges.subscribe((changed) => {
      me.ReloadDataAndRender();
    });

    me.setRatingSubscriber();
    me.ReloadDataAndRender();
  }

  public locationPanel(): void {
    const me = this;
    me.panelOpenState = !me.panelOpenState;
    me.PushChanges();
  }

  // check, if one rating of an item has been changed, then reload the view, with new data
  private setRatingSubscriber() {
    const me = this;
    me._ratingChangesSubscriber = me._ratingService.ObserveChanges.subscribe((changedRatings) => {
      let eventRatingChanged = false;
      const eventKeyPrefix = ContentType.Event + "|";
      for (const changedRating of changedRatings) {
        if (changedRating.toString().startsWith(eventKeyPrefix)) {
          eventRatingChanged = true;
          break;
        }
      }
      if (eventRatingChanged) {
        me.ReloadDataAndRender();
      }
    });
  }

  /**
  * Load Events data
  */
  private ReloadDataAndRender(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._eventService.GetEventViewModel(me.eventId)
      .then(event => {
        if (event) {
          me.hasData = true;
          me.eventViewModel = event;
          me.PushChanges();
        } else {
          me.hasData = false;
          // to avoid error for first binding
          me.eventViewModel = new EventDetailViewModel();
          me.PushChanges();
        }
        me.initialAnimation();
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(e => {
        me._coreLogService.error("EventDetailComponent.ReloadDataAndRender", "GetEventViewModel", e);
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowErrorMessageFailed(29);
      });
  }

  /**
  * Open a ModalDialog for rating an item or watching item rating info
  * @param news is empty if there is no rating yet. sending item Id and contentType to show rating data in modalDialog if user rate this item before.
  */
  public openRateModel(): void {
    const me = this;
    const ratingModel = me._bottomSheetHelperService.open(RatingDialogComponent, {
      data: { contentId: me.eventViewModel.id, contentType: ContentType.Event, title: me.eventViewModel.label },

    });
    // show snackBar after rating Item
    ratingModel.afterDismissed().subscribe((result: any) => {
      if (result) {
        result
          .then(() => {
            me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
          })
          .catch(e => {
            me._coreLogService.error("EventDetailComponent.openRateModel", "ratingModel.afterClosed", e);
            me._snackBarService.ShowErrorMessageFailed(30);
          });
      }
    });
  }

  // when countDown ends it must be hidden from the view.
  public countdownEnding(): void {
    this.PushChanges();
  }

  /** Check if the counter must be shown at the page loading.
   * it ends when the Event starts
   * @param startDate start date of the Event
   * @result send boolean to view for show or hide the counter.
   */
  public eventStarted(startDate: Moment): boolean {
    const now = moment();
    return now.valueOf() > startDate.valueOf();
  }

  /**
 * initial animation after page loading
 */
  private initialAnimation() {
    const me = this;
    setTimeout(() => {
      const headerTitleContainerElement: HTMLElement = document.getElementById("headerTitleContainer");
      const headerTitleElement: HTMLElement = document.getElementById("headerTitle");
      const shiftPixels = headerTitleContainerElement.clientWidth - headerTitleElement.clientWidth;
      if (shiftPixels < 0) {
        me.floatingTextActivated = true;
        me.shiftPixels = shiftPixels; // 20 = padding
        me.animationObject = {
          value: "right", params: {
            shiftPixels: `${(me.shiftPixels / 2) - 20}px` // 20 is padding in header-title class
            ,
            initialShift: `${((me.shiftPixels * (-1)) / 2) + 10}px`,
            initialtime: "3s",
            startPosition: "0px"
          }
        };
        me.PushChanges();
      }
    }, 500);
  }
  /**
  * creating infinite animation in angular
  * @param event animation event object
  */
  public onAnimateDone(event) {
    const me = this;
    if (event.toState === "right") {
      me.animationObject = {
        value: "left", params: {
          shiftPixels: `${(me.shiftPixels / 2) - 20}px`
          , initialShift: `${((me.shiftPixels * (-1)) / 2) + 10}px`
          , initialtime: "0s"
          , startPosition: `${((me.shiftPixels * (-1)) / 2) + 10}px`
        }
      };
      me.PushChanges();
    } else {
      me.animationObject = {
        value: "right", params: {
          shiftPixels: `${(me.shiftPixels / 2) - 20}px`
          , initialShift: `${((me.shiftPixels * (-1)) / 2) + 10}px`
          , initialtime: "0s"
          , startPosition: `${((me.shiftPixels * (-1)) / 2) + 10}px`
        }
      };
      me.PushChanges();
    }
  }

  public navigateBack(): void {
    this._routerHelperService.navigateBackSimple();
  }

  public swipeLeftHandler() {
    this._routerHelperService.navigateBackSimple();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
    if (me._ratingChangesSubscriber) {
      me._ratingChangesSubscriber.unsubscribe();
    }
    if (me._eventChangesSubscriber) {
      me._eventChangesSubscriber.unsubscribe();
    }
  }
}
